import { useCallback, useMemo, useState } from 'react';
import { Close, Fullscreen } from '@mui/icons-material';
import { IconButton, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { Modal, NothingToShow, Tooltip } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { PanelSkeleton } from '../panelSkeleton/PanelSkeleton';
import { SectionCard } from '../sectionCard/SectionCard';

import { TimeSeriesPanelProps } from './TimeSeriesPanel.types';
import * as Styled from './TimeSeriesPanel.styles';

/**
 * Time-series panel: the chart the DOD monitor is mostly made of.
 *
 * The x axis is the union of every series' timestamps, so series sampled at different times still
 * line up under one cursor. Gaps stay `null` rather than being interpolated - a missing rollup
 * bucket means "we did not measure", not "the value was zero", and drawing a line through it is
 * how a worker outage comes to look like a quiet period.
 */
export const TimeSeriesPanel = ({
  title,
  series,
  seriesLabels = {},
  seriesColors = {},
  area = false,
  stacked = false,
  height = 240,
  annotations = [],
  threshold,
  grafanaUrl,
  isLoading = false,
  error,
  actions,
  className,
  tickNumber,
}: TimeSeriesPanelProps): JSX.Element => {
  const { formatMessage } = useLocale();
  const { palette } = useTheme();
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const colorFor = useCallback(
    (key: string, index: number): string =>
      seriesColors[key] ?? palette.chart.series[index % palette.chart.series.length],
    [seriesColors, palette],
  );

  const toggleSeries = useCallback((key: string): void => {
    setHiddenKeys((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));
  }, []);

  const { xAxisData, chartSeries } = useMemo(() => {
    const timestamps = Array.from(new Set(series.flatMap((item) => item.points.map((point) => point[0])))).sort(
      (left, right) => left - right,
    );

    const visible = series.filter((item) => !hiddenKeys.includes(item.key));

    return {
      xAxisData: timestamps.map((ts) => new Date(ts)),
      chartSeries: visible.map((item) => {
        const byTimestamp = new Map<number, number | null>(item.points);
        const index = series.indexOf(item);

        return {
          id: item.key,
          label: seriesLabels[item.key] ?? item.key,
          data: timestamps.map((ts) => byTimestamp.get(ts) ?? null),
          color: colorFor(item.key, index),
          area,
          stack: stacked ? 'total' : undefined,
          showMark: false,
          connectNulls: false,
        };
      }),
    };
  }, [series, hiddenKeys, seriesLabels, colorFor, area, stacked]);

  const renderChart = (chartHeight: number): JSX.Element => (
    <LineChart
      height={chartHeight}
      xAxis={[{ data: xAxisData, scaleType: 'time', tickNumber }]}
      series={chartSeries}
      margin={{ top: 12, right: 16, bottom: 24, left: 48 }}
      slotProps={{ legend: { hidden: true } }}
      tooltip={{ trigger: 'axis' }}
      axisHighlight={{ x: 'line' }}
      grid={{ horizontal: true }}
      sx={{ '& .MuiChartsGrid-line': { stroke: palette.chart.grid } }}
    >
      {threshold && (
        <ChartsReferenceLine
          y={threshold.value}
          label={threshold.label ?? formatMessage({ id: AppMessages['dod.common.threshold'] })}
          lineStyle={{ stroke: threshold.color ?? palette.chart.bad, strokeDasharray: '6 4' }}
          labelStyle={{ fill: threshold.color ?? palette.chart.bad, fontSize: 10 }}
        />
      )}
      {annotations.map((annotation) => (
        <ChartsReferenceLine
          key={`${annotation.timestamp}-${annotation.label}`}
          x={new Date(annotation.timestamp)}
          label={annotation.label}
          lineStyle={{ stroke: annotation.color ?? palette.chart.neutral, strokeDasharray: '4 3' }}
          labelStyle={{ fill: annotation.color ?? palette.chart.neutral, fontSize: 10 }}
        />
      ))}
    </LineChart>
  );

  const headerActions = (
    <>
      {actions}
      <Styled.Legend role="group" aria-label={formatMessage({ id: AppMessages['dod.common.legend.toggle'] })}>
        {series.map((item, index) => (
          <Styled.LegendItem
            key={item.key}
            type="button"
            aria-pressed={!hiddenKeys.includes(item.key)}
            $isHidden={hiddenKeys.includes(item.key)}
            onClick={() => toggleSeries(item.key)}
          >
            <Styled.LegendSwatch $color={colorFor(item.key, index)} />
            {seriesLabels[item.key] ?? item.key}
          </Styled.LegendItem>
        ))}
      </Styled.Legend>

      {grafanaUrl && (
        <Styled.GrafanaLink href={grafanaUrl} target="_blank" rel="noreferrer noopener">
          {formatMessage({ id: AppMessages['dod.common.openInGrafana'] })}
        </Styled.GrafanaLink>
      )}

      <Tooltip title={formatMessage({ id: AppMessages['dod.common.fullscreen'] })} placement="top">
        <IconButton
          size="small"
          onClick={() => setIsFullscreen(true)}
          aria-label={formatMessage({ id: AppMessages['dod.common.fullscreen'] })}
        >
          <Fullscreen fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const renderBody = (): JSX.Element => {
    if (isLoading) {
      return <PanelSkeleton height={height} withTitle={false} />;
    }

    if (error) {
      return (
        <div role="alert">
          <NothingToShow text={formatMessage({ id: AppMessages['dod.common.error'] })} />
        </div>
      );
    }

    if (chartSeries.length === 0 || xAxisData.length === 0) {
      return <NothingToShow text={formatMessage({ id: AppMessages['dod.common.empty'] })} />;
    }

    return renderChart(height);
  };

  return (
    <>
      <SectionCard className={className} title={title} actions={headerActions} bodyHeight={height}>
        {renderBody()}
      </SectionCard>

      {isFullscreen && (
        <Modal type="plain" size="big" open={isFullscreen} onClose={() => setIsFullscreen(false)} zIndex={1400}>
          <Styled.FullscreenBody>
            <SectionCard
              title={title}
              actions={
                <IconButton
                  size="small"
                  onClick={() => setIsFullscreen(false)}
                  aria-label={formatMessage({ id: AppMessages['dod.common.exitFullscreen'] })}
                >
                  <Close fontSize="small" />
                </IconButton>
              }
            >
              {renderChart(Math.max(height * 2, 480))}
            </SectionCard>
          </Styled.FullscreenBody>
        </Modal>
      )}
    </>
  );
};
